import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, Clock } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { T, paths, DOMAIN } from "../i18n";
import { getArticle, ARTICLES } from "../data/articles";
import { fetchDbArticles } from "../api";

export default function ArticlePage({ lang }) {
  const { articleSlug } = useParams();
  const t = T[lang];
  const p = paths(lang);
  const staticArticle = getArticle(articleSlug);
  const [dbArticles, setDbArticles] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    fetchDbArticles().then((list) => { setDbArticles(list); setLoaded(true); });
  }, [articleSlug]);

  const a = staticArticle || dbArticles.find((x) => x.slug === articleSlug);
  if (!a && !loaded) {
    return <div className="mx-auto max-w-3xl px-4 py-32 text-center text-slate-500" data-testid="article-loading">…</div>;
  }
  if (!a) return <Navigate to={p.guides} replace />;

  const all = [...dbArticles, ...ARTICLES].filter((x) => x.slug !== a.slug);
  const seen = new Set();
  const related = all.filter((x) => (seen.has(x.slug) ? false : (seen.add(x.slug), true))).slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a[lang].title,
    description: a[lang].excerpt,
    datePublished: a.date,
    inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
    author: { "@type": "Organization", name: "1st Nations Security", url: DOMAIN + "/" },
    publisher: { "@type": "Organization", name: "1st Nations Security", url: DOMAIN + "/" },
    mainEntityOfPage: DOMAIN + (lang === "fr" ? `/fr/guides/${a.slug}` : `/guides/${a.slug}`)
  };

  return (
    <div data-testid={`article-page-${a.slug}`}>
      <Seo lang={lang} title={`${a[lang].title} | 1st Nations Security`} description={a[lang].excerpt}
        enPath={`/guides/${a.slug}`} frPath={`/fr/guides/${a.slug}`}
        jsonLd={[articleJsonLd, breadcrumbJsonLd([[t.breadcrumbHome, p.home], [t.nav.guides, p.guides], [a[lang].title, null]])]} />
      <PageHeader lang={lang} eyebrow={a.category[lang]} title={a[lang].title}
        breadcrumbs={[[t.breadcrumbHome, p.home], [t.nav.guides, p.guides], ["Article", null]]} />

      <article className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-testid="article-body">
        <div className="flex items-center gap-4 font-mono text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><CalendarDays size={13} /> {a.date}</span>
          <span className="flex items-center gap-1.5"><Clock size={13} /> {a.readMin} min {lang === "fr" ? "de lecture" : "read"}</span>
        </div>
        {a[lang].sections.map((s, i) => (
          <Reveal key={i} className="mt-10">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-100">{s.h2}</h2>
            {s.paras.map((para, j) => (
              <p key={j} className="mt-4 text-base leading-relaxed text-slate-300">{para}</p>
            ))}
          </Reveal>
        ))}
        <Reveal className="mt-12">
          <Link to={p.guides} className="inline-flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300" data-testid="article-back-link">
            <ArrowLeft size={15} /> {lang === "fr" ? "Tous les guides" : "All guides"}
          </Link>
        </Reveal>
      </article>

      <section className="border-t border-slate-800/60 bg-card/30 py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{lang === "fr" ? "Guides connexes" : "Related Guides"}</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={p.guide(r.slug)} className="group flex h-full flex-col rounded-2xl border border-slate-800/70 bg-obsidian/60 p-6 transition-all hover:-translate-y-1 hover:border-amber-500/40" data-testid={`related-guide-${r.slug}`}>
                <span className="rounded-full bg-amber-500/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300 self-start">{r.category[lang]}</span>
                <h3 className="mt-3 flex-1 font-display text-base font-bold leading-snug text-slate-100 group-hover:text-amber-300">{r[lang].title}</h3>
                <span className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-amber-400">{lang === "fr" ? "Lire" : "Read"} <ArrowRight size={12} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CtaBand lang={lang} />
    </div>
  );
}
